import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, PopoverController } from '@ionic/angular';

import { KnowledgeBaseCollection } from 'src/models/knowledge-base-collection.model';
import { KnowledgeBaseCollectionsService } from 'src/services/knowledge-base-collections.service';

// Interface for the tree node structure used internally and in the template
export interface CollectionTreeNode extends KnowledgeBaseCollection {
  children: CollectionTreeNode[];
  expanded?: boolean; // For UI state
  level?: number; // For indentation
  matchesFilter?: boolean; // For search filtering (true if node or descendant matches)
  nodeDirectlyMatchesFilter?: boolean; // True only if this specific node's name matches
}

@Component({
  selector: 'app-collection-selector',
  templateUrl: './collection-selector.component.html',
  styleUrls: ['./collection-selector.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, // Needed for ngModel (search bar)
    IonicModule, // Needed for Ionic components
  ],
})
export class CollectionSelectorComponent implements OnInit {
  @Input() currentCollectionId: string | null = null;
  @Input() title: string = 'Move article to...';
  @Input() showRootOption: boolean = true;
  @Input() onlyShowCollectionsWithArticles: boolean = false; // Filter out empty collections
  @Input() preFilteredCollections: KnowledgeBaseCollection[] | null = null; // Pre-filtered collections to use instead of loading all
  // Callback function provided by the presenting component
  @Input() callback?: (selectedId: string | null) => void;

  isLoading = false;
  searchTerm = '';
  allCollections: KnowledgeBaseCollection[] = [];
  collectionTree: CollectionTreeNode[] = [];
  filteredTree: CollectionTreeNode[] = []; // Tree structure displayed after filtering

  // Represents the option to place the article at the root (no collection)
  readonly rootOption = { id: null, name: 'Knowledge Base (Root)' };
  // Tracks the ID selected by the user via radio buttons
  selectedId: string | null | undefined = undefined;

  constructor(
    private collectionsService: KnowledgeBaseCollectionsService,
    private popoverCtrl: PopoverController, // Inject PopoverController to close itself
  ) {}

  async ngOnInit() {
    // Initialize selection with the current collection ID
    this.selectedId = this.currentCollectionId;
    await this.loadCollections();
  }

  async loadCollections(): Promise<void> {
    this.isLoading = true;
    this.searchTerm = ''; // Reset search on load
    try {
      // Use pre-filtered collections if provided, otherwise load all collections
      if (this.preFilteredCollections) {
        this.allCollections = this.preFilteredCollections;
      } else {
        // Conditionally filter collections based on input parameter
        const params = this.onlyShowCollectionsWithArticles ? { hasChildren: true } : {};
        this.allCollections = await this.collectionsService.getAll(params);
      }
      this.collectionTree = this.buildTree(this.allCollections);
      this.applyFilter(); // Apply filter (which will be empty initially)
    } catch (error) {
      console.error('Failed to load collections:', error);
      // TODO: Implement user-facing error feedback (e.g., Toast message)
    } finally {
      this.isLoading = false;
    }
  }

  // Client-side tree building
  private buildTree(
    collections: KnowledgeBaseCollection[],
    parentId: string | null = null,
    level = 0,
  ): CollectionTreeNode[] {
    return collections
      .filter((collection) => collection.parentId === parentId)
      .sort((a, b) => a.name.localeCompare(b.name)) // Sort siblings alphabetically
      .map((collection) => ({
        ...collection,
        level,
        expanded: false, // Start collapsed
        children: this.buildTree(collections, collection.id, level + 1),
        matchesFilter: true, // Assume matches initially
        nodeDirectlyMatchesFilter: true,
      }));
  }

  // Debounced search triggers this
  onSearchInput(): void {
    this.applyFilter();
  }

  // Filters the tree based on the searchTerm
  applyFilter(): void {
    const lowerSearchTerm = this.searchTerm.toLowerCase().trim();

    if (!lowerSearchTerm) {
      // If search is empty, reset filter states and show the full tree
      this.resetFilterState(this.collectionTree);
      this.filteredTree = this.collectionTree;
      return;
    }

    // Recursively filter the tree, marking nodes that match or have descendants that match
    this.filteredTree = this.filterAndMarkTree(this.collectionTree, lowerSearchTerm).filter(
      (node) => node.matchesFilter,
    ); // Keep only top-level nodes that match or contain matches
  }

  // Recursive function to filter tree nodes and mark matching status
  private filterAndMarkTree(nodes: CollectionTreeNode[], lowerSearchTerm: string): CollectionTreeNode[] {
    return nodes.map((node) => {
      // Check if this node's name matches the search term
      const nodeDirectlyMatches = node.name.toLowerCase().includes(lowerSearchTerm);

      // Recursively filter children
      const filteredChildren = this.filterAndMarkTree(node.children, lowerSearchTerm);

      // Check if any child (or their descendants) matches the filter
      const hasMatchingChildren = filteredChildren.some((child) => child.matchesFilter);

      // This node should be shown if it matches directly OR if it has children that match
      const matches = nodeDirectlyMatches || hasMatchingChildren;

      // Expand the node automatically if it or its children match the filter
      // Keep existing expansion if user manually expanded it before searching
      const shouldExpand = matches && lowerSearchTerm.length > 0;

      return {
        ...node,
        children: filteredChildren, // Assign the filtered children list
        matchesFilter: matches, // Mark if this node or descendants match
        nodeDirectlyMatchesFilter: nodeDirectlyMatches, // Mark if this node itself matches
        expanded: node.expanded || shouldExpand, // Preserve or set expansion based on match
      };
    }); // Don't filter here, filter happens in the caller (applyFilter)
  }

  // Helper to reset filter state recursively (used when clearing search)
  private resetFilterState(nodes: CollectionTreeNode[]): void {
    nodes.forEach((node) => {
      node.matchesFilter = true; // Reset match state
      node.nodeDirectlyMatchesFilter = true;
      // Optional: Reset expanded state when clearing filter, or preserve user expansion
      // node.expanded = false;
      this.resetFilterState(node.children);
    });
  }

  toggleExpand(node: CollectionTreeNode, event: MouseEvent): void {
    event.stopPropagation(); // Prevent row selection when clicking icon
    node.expanded = !node.expanded;
  }

  selectCollection(id: string | null): void {
    this.selectedId = id;
    // Could potentially auto-confirm and close here if desired
    // this.confirmSelection();
  }

  // Called when the 'Move Here' button is clicked
  confirmSelection(): void {
    if (this.callback) {
      // Pass the selected ID (which could be null for the root)
      this.callback(this.selectedId);
    }
    // Dismiss the popover, optionally passing data back
    this.popoverCtrl.dismiss({ selectedId: this.selectedId });
  }

  // Called when the 'Cancel' button is clicked
  cancel(): void {
    this.popoverCtrl.dismiss(); // Dismiss without passing data
  }

  // Helper for dynamic indentation in the template
  getIndentStyle(level: number): { [key: string]: string } {
    // Use padding-left for indentation of the whole item content
    return { 'padding-left': `${level * 20}px` }; // Adjust multiplier as needed
  }

  // --- Optional: Add New Collection Placeholder ---
  async addNewCollection(parentId: string | null = null, event?: MouseEvent): Promise<void> {
    event?.stopPropagation(); // Prevent selecting the row if called from a button
    // TODO: Implement UI for creating a new collection (e.g., prompt, inline input, or modal)
    console.log('Request to add new collection under parent:', parentId);
    // Example workflow:
    // 1. Show input/prompt for collection name
    // 2. Call `this.collectionsService.create({ name, parentId })`
    // 3. Call `this.loadCollections()` to refresh the tree
    // 4. Potentially select the newly created collection automatically
  }
}
